import { useState, useEffect } from 'react';
import moment from 'moment';
import { isWeb } from '@helpers';
import { Popup } from '@components/machines/components/timeline';
import { AlarmEvent, Event, PopupConfig, TriggerShowPopup, TimelineSizes } from '@interfaces/timeline';
import { ProductionBatch } from '@interfaces/search';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';

// Global popup timeout variables
let popupTimeoutProductivityStatus: null | ReturnType<typeof setTimeout> = null;
let popupTimeoutProductivityStatusLabel: null | ReturnType<typeof setTimeout> = null;
let popupTimeoutAlarm: null | ReturnType<typeof setTimeout> = null;
let popupTimeoutNcFileName: null | ReturnType<typeof setTimeout> = null;
let initTime: null | string = null;

interface UseManageTimelinePopupParams {
  isDragging?: boolean;
  sizes: Pick<
    TimelineSizes,
    | 'blockWidth'
    | 'popupWidthProductivityStatus'
    | 'popupWidthProductivityStatusLabel'
    | 'popupWidthProductivityStatusLabelSymbolSize'
    | 'popupWidthAlarm'
    | 'popupWidthAlarmSymbolSize'
    | 'popupWidthNcFileName'
    | 'popupWidthNcFileNameSymbolSize'
  >;
}

export const useManageTimelinePopup = ({ isDragging = false, sizes }: UseManageTimelinePopupParams) => {
  const {
    blockWidth,
    popupWidthProductivityStatus,
    popupWidthProductivityStatusLabel,
    popupWidthProductivityStatusLabelSymbolSize,
    popupWidthAlarm,
    popupWidthAlarmSymbolSize,
    popupWidthNcFileName,
    popupWidthNcFileNameSymbolSize,
  } = sizes;

  const selectedMachine = useSelector(selectSelectedItem);

  // State
  const [popupConfigProductivityStatus, setPopupConfigProductivityStatus] = useState<PopupConfig<Event>>({ show: false });
  const [popupConfigProductivityStatusLabel, setPopupConfigProductivityStatusLabel] = useState<PopupConfig<Event>>({ show: false });
  const [popupConfigAlarm, setPopupConfigAlarm] = useState<PopupConfig<AlarmEvent>>({ show: false });
  const [popupConfigNcFileName, setPopupConfigNcFileName] = useState<PopupConfig<ProductionBatch>>({ show: false });

  // Cleanup function to clear all timeouts on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutProductivityStatus) {
        clearTimeout(popupTimeoutProductivityStatus);
        popupTimeoutProductivityStatus = null;
      }
      if (popupTimeoutProductivityStatusLabel) {
        clearTimeout(popupTimeoutProductivityStatusLabel);
        popupTimeoutProductivityStatusLabel = null;
      }
      if (popupTimeoutAlarm) {
        clearTimeout(popupTimeoutAlarm);
        popupTimeoutAlarm = null;
      }
      if (popupTimeoutNcFileName) {
        clearTimeout(popupTimeoutNcFileName);
        popupTimeoutNcFileName = null;
      }
    };
  }, []);

  const clearPopupTimeout = (type: Popup) => {
    let popupTimeout;

    switch (type) {
      case 'productivityStatus':
        popupTimeout = popupTimeoutProductivityStatus;
        break;
      case 'alarm':
        popupTimeout = popupTimeoutAlarm;
        break;
      case 'ncFileName':
        popupTimeout = popupTimeoutNcFileName;
        break;
      default:
        return;
    }

    if (popupTimeout) {
      clearTimeout(popupTimeout);
    }
  };

  const triggerHidePopup = (type: Popup) => {
    let popupTimeout;
    let setConfig;

    switch (type) {
      case 'productivityStatus':
        popupTimeout = popupTimeoutProductivityStatus;
        setConfig = setPopupConfigProductivityStatus;
        break;
      case 'productivityStatusLabel':
        popupTimeout = popupTimeoutProductivityStatusLabel;
        setConfig = setPopupConfigProductivityStatusLabel;
        break;
      case 'alarm':
        popupTimeout = popupTimeoutAlarm;
        setConfig = setPopupConfigAlarm;
        break;
      case 'ncFileName':
        popupTimeout = popupTimeoutNcFileName;
        setConfig = setPopupConfigNcFileName;
        break;
      default:
        return;
    }
    if (popupTimeout) {
      clearTimeout(popupTimeout);
    }
    popupTimeout = setTimeout(() => {
      if (setConfig) {
        setConfig({ show: false });
      }
    }, 300);
    switch (type) {
      case 'productivityStatus':
        popupTimeoutProductivityStatus = popupTimeout;
        break;
      case 'productivityStatusLabel':
        popupTimeoutProductivityStatusLabel = popupTimeout;
        break;
      case 'alarm':
        popupTimeoutAlarm = popupTimeout;
        break;
      case 'ncFileName':
        popupTimeoutNcFileName = popupTimeout;
        break;
      default:
        return;
    }
  };

  const triggerShowPopup: TriggerShowPopup = (config: any, type: Popup) => {
    let popupTimeout;
    let setPopupConfig;
    let popupWidth;
    let paddingHorizontal;
    let delay;
    let widthOffset;

    const { position, width, item } = config;

    switch (type) {
      case 'productivityStatus':
        popupTimeout = popupTimeoutProductivityStatus;
        setPopupConfig = setPopupConfigProductivityStatus;
        popupWidth = popupWidthProductivityStatus;
        paddingHorizontal = 0;
        widthOffset = width / 2;

        const lastTimeOpened = initTime ? moment().diff(moment(initTime), 'milliseconds') : 0;
        delay = initTime ? (lastTimeOpened > 1000 ? 500 : 1000 - lastTimeOpened) : 1;
        break;

      case 'productivityStatusLabel':
        popupTimeout = popupTimeoutProductivityStatusLabel;
        setPopupConfig = setPopupConfigProductivityStatusLabel;
        popupWidth = config?.item?.ncFileMain?.length * popupWidthProductivityStatusLabelSymbolSize + popupWidthProductivityStatusLabel;
        paddingHorizontal = 0;
        widthOffset = 0;
        delay = 300;
        break;

      case 'alarm':
        popupTimeout = popupTimeoutAlarm;
        setPopupConfig = setPopupConfigAlarm;
        popupWidth = config?.item?.errorText?.length * popupWidthAlarmSymbolSize + popupWidthAlarm;
        paddingHorizontal = 0;
        widthOffset = 0;
        delay = 300;
        break;

      case 'ncFileName':
        popupTimeout = popupTimeoutNcFileName;
        setPopupConfig = setPopupConfigNcFileName;
        popupWidth = config?.item?.ncFileMain?.length * popupWidthNcFileNameSymbolSize + popupWidthNcFileName;
        paddingHorizontal = 0;
        widthOffset = 0;
        delay = 100;
        break;

      default:
        return;
    }

    if (popupTimeout) {
      clearTimeout(popupTimeout);
    }

    if (isDragging) {
      return;
    }

    // Calculate position
    let left = position.left + paddingHorizontal + widthOffset - popupWidth / 2;
    if (left < paddingHorizontal) {
      left = paddingHorizontal;
    } else {
      const maxLeft = blockWidth - paddingHorizontal - popupWidth;
      if (left > maxLeft) {
        left = maxLeft;
      }
    }

    let top = position.top + 40;
    initTime = moment().format();

    const result = () => {
      if (setPopupConfig) {
        setPopupConfig({
          show: true,
          position: {
            left,
            top,
          },
          elementPosition: {
            top,
            left: position.left + paddingHorizontal + widthOffset - left,
          },
          isBottom: true,
          item: {
            machineId: selectedMachine?.machineId,
            ...item,
          },
        });
      }
    };

    if (isWeb) {
      popupTimeout = setTimeout(() => {
        result();
      }, delay);

      switch (type) {
        case 'productivityStatus':
          popupTimeoutProductivityStatus = popupTimeout;
          break;
        case 'productivityStatusLabel':
          popupTimeoutProductivityStatusLabel = popupTimeout;
          break;
        case 'alarm':
          popupTimeoutAlarm = popupTimeout;
          break;
        case 'ncFileName':
          popupTimeoutNcFileName = popupTimeout;
          break;
        default:
          return;
      }
    } else {
      result();
    }
  };

  return {
    // State
    popupConfigProductivityStatus,
    popupConfigProductivityStatusLabel,
    popupConfigAlarm,
    popupConfigNcFileName,

    // Setters
    setPopupConfigProductivityStatus,
    setPopupConfigProductivityStatusLabel,
    setPopupConfigAlarm,
    setPopupConfigNcFileName,

    // Actions
    clearPopupTimeout,
    triggerHidePopup,
    triggerShowPopup,
  };
};
